/**
 * Language to Flag Country Code Mapping
 *
 * Maps ISO 639-1 language codes to ISO 3166-1 alpha-2 country codes
 * for use with flag CDN services and emoji flags.
 *
 * @module languageFlags
 */

/**
 * Maps language codes to country codes for flag display
 * Used with flagcdn.com: https://flagcdn.com/{countryCode}.svg
 */
export const LANGUAGE_TO_FLAG_MAP: Record<string, string> = {
  // Major languages
  en: 'gb',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
  pt: 'pt',
  ru: 'ru',
  zh: 'cn',
  ja: 'jp',
  ko: 'kr',

  // Additional languages
  ar: 'sa',
  cs: 'cz',
  pl: 'pl',
  tr: 'tr',
  uk: 'ua',
  th: 'th',
  vi: 'vn',
  id: 'id',
  hi: 'in',
  nl: 'nl',
  sv: 'se',
  da: 'dk',
  no: 'no',
  fi: 'fi',
  el: 'gr',
  he: 'il',
  ms: 'my',

  // Regional variants
  ar_AE: 'ae',
  ar_EG: 'eg',
  ar_SA: 'sa',
  en_US: 'us',
  en_GB: 'gb',
  pt_BR: 'br',
  pt_PT: 'pt',
  zh_CN: 'cn',
  zh_TW: 'tw',
} as const;

/**
 * Maps language codes to Unicode flag emojis
 */
export const LANGUAGE_FLAG_EMOJIS: Record<string, string> = {
  cs: '🇨🇿',
  ar: '🇸🇦',
  en: '🇬🇧',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  ja: '🇯🇵',
  zh: '🇨🇳',
  ru: '🇷🇺',
  it: '🇮🇹',
  pt: '🇵🇹',
  ko: '🇰🇷',
  tr: '🇹🇷',
  pl: '🇵🇱',
  nl: '🇳🇱',
  sv: '🇸🇪',
  da: '🇩🇰',
  no: '🇳🇴',
  fi: '🇫🇮',
  el: '🇬🇷',
  he: '🇮🇱',
  hi: '🇮🇳',
  th: '🇹🇭',
  vi: '🇻🇳',
  id: '🇮🇩',
  ms: '🇲🇾',
  uk: '🇺🇦',
} as const;

/**
 * Get flag country code from language code
 *
 * @param code - Language code (ISO 639-1 or with regional variant)
 * @param fallback - Fallback country code if not found (default: 'gb')
 * @returns Country code for flag display
 *
 * @example
 * ```ts
 * getFlagCode('cs') // => 'cz'
 * getFlagCode('ar_SA') // => 'sa'
 * getFlagCode('unknown', 'us') // => 'us'
 * ```
 */
export function getFlagCode(code: string | undefined | null, fallback = 'gb'): string {
  if (!code) return fallback;
  const normalized = code.toLowerCase();

  // Try exact match (handles regional variants like ar_SA)
  if (LANGUAGE_TO_FLAG_MAP[normalized]) {
    return LANGUAGE_TO_FLAG_MAP[normalized];
  }

  // Try base code (ar_SA -> ar)
  const baseCode = normalized.split(/[-_]/)[0];
  return LANGUAGE_TO_FLAG_MAP[baseCode] || fallback;
}

/**
 * Get flag emoji from language code
 *
 * @param code - Language code (ISO 639-1)
 * @param fallback - Fallback emoji if not found (default: '🌐')
 * @returns Unicode flag emoji
 *
 * @example
 * ```ts
 * getFlagEmoji('cs') // => '🇨🇿'
 * getFlagEmoji('unknown') // => '🌐'
 * ```
 */
export function getFlagEmoji(code: string | undefined | null, fallback = '🌐'): string {
  if (!code) return fallback;
  const normalized = code.toLowerCase();

  if (LANGUAGE_FLAG_EMOJIS[normalized]) {
    return LANGUAGE_FLAG_EMOJIS[normalized];
  }

  const baseCode = normalized.split(/[-_]/)[0];
  return LANGUAGE_FLAG_EMOJIS[baseCode] || fallback;
}

/**
 * Get flagcdn.com URL for a language code
 *
 * @param code - Language code (ISO 639-1 or with regional variant)
 * @param size - Image size ('w20' | 'w40' | 'w80' | 'w160'), default 'w80'
 * @returns Full URL to flag image on flagcdn.com
 *
 * @example
 * ```ts
 * getFlagUrl('cs') // => 'https://flagcdn.com/w80/cz.png'
 * getFlagUrl('ar_SA', 'w160') // => 'https://flagcdn.com/w160/sa.png'
 * ```
 */
export function getFlagUrl(
  code: string | undefined | null,
  size: 'w20' | 'w40' | 'w80' | 'w160' = 'w80'
): string {
  const countryCode = getFlagCode(code);
  // flagcdn uses .png for sized images, .svg only for base path
  return `https://flagcdn.com/${size}/${countryCode}.png`;
}
